import { useEffect, useState } from 'react';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigateToReferral = () => {
    window.REACT_APP_NAVIGATE('/referral-program');
  };

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src="https://static.readdy.ai/image/2cff082c3240b86a1a45c3f20eab75c2/c96dfd15737c1667350c6dc0264ad9a9.jpeg" 
              alt="Mercedes with Jacob Logo" 
              className="h-12 w-12 object-cover rounded-full ring-2 ring-amber-400 hidden md:block"
            />
            <span className="text-white font-semibold text-lg tracking-wide">Mercedes with Jacob</span>
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-amber-400 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer">About Me</button>
            <button onClick={() => scrollToSection('inventory')} className="text-gray-300 hover:text-amber-400 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer">Inventory</button>
            <button onClick={() => scrollToSection('scheduler')} className="text-gray-300 hover:text-amber-400 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer">Schedule</button>
            <button onClick={() => scrollToSection('social')} className="text-gray-300 hover:text-amber-400 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer">Connect</button>
          </div>
        </div>
      </nav>

      {/* Hero Background Image - Desktop */}
      <div className="absolute inset-0 hidden md:block">
        <img 
          src="https://public.readdy.ai/ai/img_res/c08a7a78d1ac2206113f8a107110aa64.jpg" 
          alt="2025 AMG G63 4x4 Squared"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80"></div>
      </div>

      {/* Hero Background Image - Mobile */}
      <div className="absolute inset-0 md:hidden">
        <img 
          src="https://readdy.ai/api/search-image?query=powerful%202025%20mercedes%20benz%20amg%20g63%204x4%20squared%20luxury%20suv%20front%20three%20quarter%20view%20in%20obsidian%20black%20metallic%20with%20dramatic%20studio%20lighting%20showcasing%20the%20vehicles%20lifted%20portal%20axles%20massive%20off%20road%20presence%20aggressive%20wide%20body%20kit%20and%20iconic%20mercedes%20grille%20premium%20automotive%20photography%20with%20golden%20amber%20accent%20lights%20against%20sophisticated%20dark%20gradient%20background%20high%20end%20dealership%20aesthetic%20with%20professional%20shadows%20creating%20depth&width=800&height=1200&seq=heromobile2025g63&orientation=portrait" 
          alt="2025 AMG G63 4x4 Squared"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80"></div>
      </div>

      {/* Hero Content */}
      <div className="relative z-10 h-full flex items-center justify-center">
        <div className="text-center px-6 max-w-4xl">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
            Experience Luxury<br />
            <span className="text-amber-400">with Jacob</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-10 font-light">
            Your trusted Mercedes-Benz sales specialist for G-Class, AMG, and Maybach excellence
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={() => scrollToSection('inventory')}
              className="px-8 py-4 bg-amber-400 text-black font-semibold rounded-lg hover:bg-amber-500 transition-all duration-300 shadow-lg hover:shadow-amber-400/50 whitespace-nowrap cursor-pointer"
            >
              View Inventory
            </button>
            <button 
              onClick={() => scrollToSection('scheduler')}
              className="px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Schedule Appointment/Call
            </button>
          </div>
          
          {/* Referral CTA */}
          <div className="mt-8">
            <button 
              onClick={navigateToReferral}
              className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm border border-amber-400/50 text-amber-400 font-medium rounded-lg hover:bg-amber-400 hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer group"
            >
              <i className="ri-gift-line mr-2 text-lg group-hover:scale-110 transition-transform"></i>
              Refer a buyer, earn a bonus
              <i className="ri-arrow-right-line ml-2 text-lg group-hover:translate-x-1 transition-transform"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="animate-bounce">
          <i className="ri-arrow-down-line text-white text-3xl"></i>
        </div>
      </div>
    </section>
  );
}
